import React from 'react';

interface GigHeaderProps {
  collectionName: string;
  currentSongTitle: string;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onOpenChart: () => void;
  onOpenSetlist: () => void;
  showEditionModeButton?: boolean;
  onEditionMode?: () => void;
  onSettings?: () => void;
  hasScore?: boolean;
  onViewScore?: () => void;
}

// Proportional left/width derived from Figma 402px reference frame (#312:299)
const p = (px: number) => `${(px / 402 * 100).toFixed(3)}%`;

const GigHeader: React.FC<GigHeaderProps> = ({
  collectionName,
  currentSongTitle,
  canNavigatePrev,
  canNavigateNext,
  onNavigatePrev,
  onNavigateNext,
  onOpenChart,
  onOpenSetlist,
  showEditionModeButton = false,
  onEditionMode,
  onSettings,
  hasScore = false,
  onViewScore,
}) => {

  return (
    <div style={{ position: 'relative', width: '100%', height: '131px' }}>

      {/* Teal banner — Figma #317:426: 402×19, bg #53D2D4 */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '19px', backgroundColor: '#53D2D4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* group #427:45 centered: 65.66×12.81 — icon at (0,0) + text at (15.55,0.71) relative to group */}
        <div style={{ position: 'relative', width: '65.66px', height: '12.81px', flexShrink: 0 }}>
          <img
            src="/figma-assets/icons/gig-header/teal-banner-icon.svg"
            width="13" height="13" alt=""
            style={{ position: 'absolute', left: 0, top: 0 }}
          />
          <span style={{
            position: 'absolute', left: '15.55px', top: '0.71px',
            width: '50.11px', height: '10.37px',
            fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500,
            fontSize: '8.64px', letterSpacing: '0.05em', textTransform: 'uppercase',
            color: '#184C4C', textAlign: 'center', lineHeight: '10.37px',
          }}>
            gig mode
          </span>
        </div>
      </div>

      {/* Header bar — Figma #346:57: 402×48, bg #3D3028, at y:19 */}
      <div style={{ position: 'absolute', left: 0, top: '19px', width: '100%', height: '48px', backgroundColor: '#3D3028' }}>
        {/* Back — #430:156: x:19, y:31(frame)→y:12(bar), 24.64×24.64, fill #9DDB17 */}
        {showEditionModeButton && onEditionMode && (
          <button
            onClick={onEditionMode}
            title="Return to Edition Mode"
            style={{ position: 'absolute', left: p(19), top: '12px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img src="/figma-assets/icons/gig-header/back-header.svg" width="25" height="25" alt="Edition Mode" style={{ display: 'block' }} />
          </button>
        )}
        {/* OPEN composite — #430:182: x:91, y:31(frame)→y:12(bar), 76.96×30.69 */}
        <button
          onClick={onOpenChart}
          title="Open Chart"
          style={{ position: 'absolute', left: p(91), top: '12px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src="/figma-assets/icons/gig-header/open-composite.svg" width="77" height="31" alt="Open" style={{ display: 'block' }} />
        </button>
        {/* SETLISTS composite — #430:183: x:216, y:32.4(frame)→y:13.4(bar), 93.42×24.64 */}
        <button
          onClick={onOpenSetlist}
          title="Open Setlist"
          style={{ position: 'absolute', left: p(216), top: '13px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src="/figma-assets/icons/gig-header/setlists-composite.svg" width="94" height="25" alt="Setlists" style={{ display: 'block' }} />
        </button>
        {/* Hamburger — #430:184: x:357, y:35(frame)→y:16(bar), 27×19.2, fill #DB9817 */}
        {onSettings && (
          <button
            onClick={onSettings}
            title="Menu"
            style={{ position: 'absolute', left: p(357), top: '16px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img src="/figma-assets/icons/gig-header/hamburger-header.svg" width="27" height="20" alt="Menu" style={{ display: 'block' }} />
          </button>
        )}
        {/* Voir la partition — fonctionnel, visible uniquement si le morceau courant a un enregistrement MiniDaw */}
        {hasScore && onViewScore && (
          <button
            onClick={onViewScore}
            title="Voir la partition"
            style={{ position: 'absolute', left: p(318), top: '10px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img src="/figma-assets/edit-mode/icon-piano-rec.svg" width="28" height="28" alt="Voir la partition" style={{ display: 'block' }} />
          </button>
        )}
      </div>

      {/* Setlist name — Figma #312:337: x:57, y:73, 289×29 — Kumbh Sans 500 13px SMALL_CAPS 0.17em #D3CCBD CENTER */}
      <div style={{
        position: 'absolute', left: p(57), top: '73px', width: p(289), height: '29px',
        fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '13px',
        letterSpacing: '0.17em', fontVariantCaps: 'small-caps', color: '#D3CCBD',
        textAlign: 'center', lineHeight: '29px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {collectionName || 'NO SETLIST'}
      </div>

      {/* Title pill — Figma #312:310: x:11, y:97, 380×34 */}
      <div style={{ position: 'absolute', left: p(11), top: '97px', width: p(380), height: '34px' }}>
        {/* Pill rect #I312:310;268:309: y:2, 380×24, bg #D9D9D9, radius 23px */}
        <div style={{ position: 'absolute', left: 0, top: '2px', width: '100%', height: '24px', backgroundColor: '#D9D9D9', borderRadius: '23px' }} />
        {/* Prev nav — functional, not in Figma state_0 */}
        <button
          onClick={onNavigatePrev}
          disabled={!canNavigatePrev}
          title="Previous song"
          style={{ position: 'absolute', left: '8px', top: '2px', height: '24px', background: 'none', border: 'none', padding: '0 4px', cursor: canNavigatePrev ? 'pointer' : 'default', opacity: canNavigatePrev ? 1 : 0.3, zIndex: 1 }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#271B13" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        {/* Title text #I312:310;268:310: x:50, y:2.67, 280×23.33 — Kumbh Sans 400 24px SMALL_CAPS #271B13 CENTER */}
        <span style={{
          position: 'absolute', left: '50px', top: '2.67px',
          width: 'calc(100% - 100px)', height: '23.33px',
          fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400, fontSize: '24px',
          fontVariantCaps: 'small-caps', color: '#271B13',
          textAlign: 'center', lineHeight: '23.33px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {currentSongTitle || 'NO SONG SELECTED'}
        </span>
        {/* Next nav — functional */}
        <button
          onClick={onNavigateNext}
          disabled={!canNavigateNext}
          title="Next song"
          style={{ position: 'absolute', right: '8px', top: '2px', height: '24px', background: 'none', border: 'none', padding: '0 4px', cursor: canNavigateNext ? 'pointer' : 'default', opacity: canNavigateNext ? 1 : 0.3, zIndex: 1 }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#271B13" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default GigHeader;
